// components/shared/banque/TransfertDetailsModal.tsx
import React from "react";
import {
  X,
  CheckCircle,
  Building2,
  Wallet,
  Calendar,
  RefreshCw,
  FileText,
  Banknote,
  Landmark,
  User,
} from "lucide-react";
import useUser from "../../../../hooks/administration/users/useUser";
import { BankTransfertItem } from "../../../../types/features/administration/VirementBancaire";

interface TransfertDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: BankTransfertItem;
  onStatusChange?: () => void;
}

export default function TransfertDetailsModal({ isOpen, onClose, item, onStatusChange }: TransfertDetailsModalProps) {
  const { transfert, details } = item;
  const { user: sender } = useUser({ userId: transfert.user_id });

  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatMoney = (amount: number, currency: "CAD" | "XAF" = "XAF") => {
    if (currency === "CAD") {
      return `${amount.toLocaleString("fr-FR")} CAD`;
    }
    return `${amount.toLocaleString("fr-FR")} FCFA`;
  };

  const handleManualPayout = () => {
    console.log("Paiement manuel effectué pour:", transfert.id);
    if (onStatusChange) onStatusChange();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Landmark size={20} />
            Détails du transfert
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Section Transfert */}
          <div className="border-b pb-4">
            <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FileText size={18} />
              Informations générales
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500">Référence</p>
                <p className="font-mono text-sm">{transfert.txt_reference}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Statut</p>
                <p className="font-medium">{transfert.status}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Calendar size={12} />
                  Créé le
                </p>
                <p className="text-sm">{formatDate(transfert.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <RefreshCw size={12} />
                  Dernière mise à jour
                </p>
                <p className="text-sm">{formatDate(transfert.updated_at)}</p>
              </div>
            </div>
          </div>

          {/* Section Montants */}
          <div className="border-b pb-4">
            <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Banknote size={18} />
              Détails financiers
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Montant envoyé (Canada) :</span>
                <span className="font-medium">{formatMoney(transfert.sending_amount, "CAD")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Frais :</span>
                <span className="text-red-600">{formatMoney(transfert.fee, "CAD")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total débité :</span>
                <span className="font-medium">{formatMoney(transfert.total_amount, "CAD")}</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 flex items-center gap-1">
                    <RefreshCw size={14} />
                    Taux de conversion :
                  </span>
                  <span>1 CAD = {transfert.conversion_rate.toLocaleString("fr-FR")} FCFA</span>
                </div>
                <div className="flex justify-between font-bold text-lg mt-2">
                  <span className="text-gray-800">Montant reçu (Cameroun) :</span>
                  <span className="text-green-600">{formatMoney(transfert.receiving_amount, "XAF")}</span>
                </div>
              </div>
            </div>
            {transfert.description && transfert.description !== "string" && (
              <div className="mt-3">
                <p className="text-xs text-gray-500">Description</p>
                <p className="text-sm italic">{transfert.description}</p>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <User size={18} />
              Informations de l'envoyeur
            </h3>

            <div className="bg-blue-50 rounded-lg p-4 space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Nom et prenom</p>
                  <p className="font-medium">
                    {sender?.last_name} {sender?.first_name}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 flex items-center gap-1">Email</p>
                  <p className="font-medium">{sender?.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Numéro de tel</p>
                  <p className="font-mono text-sm">{sender?.phonenumber || "-"}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Statut kyc </p>
                  <p className="font-medium">{sender?.has_kyc ? "Oui" : "Non"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Section Bancaire */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Building2 size={18} />
              Informations bancaires
            </h3>
            <div className="bg-blue-50 rounded-lg p-4 space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Banque</p>
                  <p className="font-medium">{details.bank_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Wallet size={12} />
                    Titulaire du compte
                  </p>
                  <p className="font-medium">{details.bank_account_owner}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Numéro de compte</p>
                  <p className="font-mono text-sm">{details.account_number}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Code guichet</p>
                  <p>{details.branch_number}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Clé de sécurité</p>
                  <p className="font-mono">{details.security_key}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Statut du paiement</p>
                  <p className="font-medium">{details.status}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition flex items-center gap-2"
          >
            <X size={16} />
            Fermer
          </button>
          {details.status === "PENDING_MANUAL_PAYOUT" && (
            <button
              onClick={handleManualPayout}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2"
            >
              <CheckCircle size={16} />
              Marquer comme payé
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
